export const CATALOG_IMPORT_FIELDS = [
  'name',
  'price',
  'unit',
  'brand',
  'country',
  'category',
  'externalSku',
  'minShipmentQty',
  'packageSize',
] as const

export type CatalogImportField = (typeof CATALOG_IMPORT_FIELDS)[number]

export const catalogImportFieldLabel = (field: CatalogImportField): string => {
  switch (field) {
    case 'name':
      return 'Наименование'
    case 'externalSku':
      return 'Артикул'
    case 'brand':
      return 'Бренд'
    case 'country':
      return 'Страна'
    case 'unit':
      return 'Ед. изм.'
    case 'price':
      return 'Цена'
    case 'minShipmentQty':
      return 'Мин. отгрузка'
    case 'packageSize':
      return 'Упаковка'
    case 'category':
      return 'Категория'
    default:
      return field
  }
}

export const mappingSourceLabel = (source: string | null | undefined): string => {
  switch (source) {
    case 'saved_profile':
      return 'Сохранённый профиль'
    case 'learned':
      return 'Обучено по прошлым импортам'
    case 'auto':
      return 'Синонимы заголовка'
    case 'embedding':
      return 'Распознавание заголовка (AI)'
    case 'manual':
      return 'Выбрано вручную'
    case 'none':
      return 'Не сопоставлено'
    default:
      return source ?? '—'
  }
}

/** Подпись типа определения для одной позиции (таблица, фильтр, импорт). */
export const matchDeterminationLabel = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'auto':
      return 'Определён автоматически'
    case 'suggested':
      return 'Требует внимания'
    case 'manual':
      return 'Задан вручную'
    case 'unmatched':
      return 'Без типа'
    default:
      return status
  }
}

export const importMatchDecisionLabel = matchDeterminationLabel

export type ImportColumnDto = {
  index: number
  header: string
  sampleValues: string[]
}

export type ProposedMappingDto = {
  field: string
  columnIndex: number | null
  columnHeader: string | null
  confidence: number | null
  source: string | null
  required: boolean
}

export type MatchCandidateDto = {
  productTypeId: number
  code: string
  name: string
  score: number
}

export type MatchPreviewDto = {
  matcherVersion: string
  decision: string
  candidates: MatchCandidateDto[]
}

export type ExistingItemUpdateMode = 'price_only' | 'accept_match'

export type ImportPreviewRowDto = {
  rowNumber: number
  name: string
  externalSku: string | null
  brand: string | null
  country: string | null
  unit: string | null
  price: string | null
  minShipmentQty: string | null
  packageSize: string | null
  category: string | null
  matchPreview: MatchPreviewDto
  /**
   * Тип позиции ДО этого импорта — то же значение, что бэк возьмёт за previousTypeId,
   * когда будет выводить причину правки. Считать смену типа от верхнего кандидата
   * матчера нельзя: подсказка расходилась с тем, что записывалось.
   */
  previousProductTypeId: number | null
  previousPrice?: string | null
  isUpdate: boolean
  isNew?: boolean
  hasPriceChange?: boolean
  hasMatchingChange: boolean
}

export type CatalogImportPreviewDto = {
  previewToken: string
  organizationId: number
  filename: string
  columns: ImportColumnDto[]
  proposedMapping: ProposedMappingDto[]
  sampleRows: ImportPreviewRowDto[]
  totalRows: number
  warnings: string[]
  savedProfileApplied: boolean
  expiresAt: string | null
  matchingChangeCount: number
  matchingChangeCountComplete: boolean
}

export type CatalogImportPreviewRowsDto = {
  rows: ImportPreviewRowDto[]
  offset: number
  limit: number
  totalRows: number
  filteredTotalRows?: number
  hasMore: boolean
  expiresAt: string | null
  matchingChangeCount: number
  matchingChangeCountComplete: boolean
  matchingChangeRowNumbers: number[]
  importSummaryComplete?: boolean
  priceChangeCount?: number
  priceChangeRowNumbers?: number[]
  newItemCount?: number
  newItemRowNumbers?: number[]
}

export type CatalogImportPreviewReviewStateDto = {
  columnMapping: ColumnMapping
  rowProductTypeOverrides: Record<number, number>
  rowFieldOverrides: Record<number, CatalogImportRowFieldOverride>
  excludedRowNumbers: number[]
  offset: number
  matchingChangesFirst: boolean
  matchingChangeRowNumbers: number[]
  privateSupplierId: number | null
  savedAt: string | null
}

export type CatalogImportPreviewSessionDto = {
  preview: CatalogImportPreviewDto
  review: CatalogImportPreviewReviewStateDto
}

export type SaveCatalogImportPreviewResultDto = {
  previewToken: string
  expiresAt: string | null
  savedAt: string | null
  offset: number
}

export type CatalogImportConfirmResultDto = {
  imported: number
  autoMatched: number
  suggested: number
  unmatched: number
  updatedExisting: number
}

export type CatalogImportRowFieldOverride = {
  name?: string
  brand?: string
  price?: string
  country?: string
  unit?: string
  minShipmentQty?: string
  packageSize?: string
  comment?: string
}

export type ColumnMapping = Record<string, number | null>

export const columnMappingFromPreview = (
  preview: CatalogImportPreviewDto,
): ColumnMapping => {
  const mapping: ColumnMapping = {}
  for (const field of CATALOG_IMPORT_FIELDS) {
    mapping[field] = null
  }
  for (const item of preview.proposedMapping) {
    if (item.columnIndex != null) {
      mapping[item.field] = item.columnIndex
    }
  }
  return mapping
}

export const mappingPayload = (mapping: ColumnMapping): Record<string, number> => {
  const payload: Record<string, number> = {}
  for (const [field, index] of Object.entries(mapping)) {
    if (index != null) {
      payload[field] = index
    }
  }
  return payload
}
